// ChatPanel — Aidea Assistant chat UI shell (Task B2a).
//
// A branded, production-grade floating chat popup styled after the
// assistant-ui shadcn template: a collapsible thread rail, a centered
// greeting + suggestion cards when empty, a message list, and a composer.
//
// Runtime: a REAL AG-UI transport. The composer sends through assistant-ui's
// useAgUiRuntime + @ag-ui/client HttpAgent, which POSTs an AG-UI RunAgentInput
// and streams back the SSE event sequence (RUN_STARTED → TEXT_MESSAGE_* →
// RUN_FINISHED). No target is assumed by default; see resolveAgUiUrl below
// for the override knobs and what happens when none resolve.
//
// Styling: every Tailwind utility is `tw-`-prefixed and emitted only under the
// `.aidea-chat-root` element (see tailwind.config.js — preflight off, prefix
// `tw-`, `important: ".aidea-chat-root"`). Brand tokens are CSS variables
// defined on `.aidea-chat-root` in chat.css and referenced via arbitrary
// values, e.g. `tw-bg-[var(--aidea-primary)]`. Nothing here can leak into the
// editor's global styles.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AssistantRuntimeProvider,
  ThreadPrimitive,
  ComposerPrimitive,
  MessagePrimitive,
  useThread,
  useMessage,
  useComposerRuntime,
  fromThreadMessageLike,
} from "@assistant-ui/react";
import { useAgUiRuntime } from "@assistant-ui/react-ag-ui";
import { HttpAgent } from "@ag-ui/client";

import i18n from "../../../locales/i18n";

import "./chat.css";
import PreviewCard from "./PreviewCard";
import { agUiFetch } from "./agUiFetch";
import { renderMiniMarkdown } from "./miniMarkdown";
import { RecommendFormatsToolUI } from "./tools/RecommendFormatsToolUI";
import { GenerateImageToolUI } from "./tools/GenerateImageToolUI";
import { SuggestImageStylesToolUI } from "./tools/SuggestImageStylesToolUI";
import { SuggestInfographicToolUI } from "./tools/SuggestInfographicToolUI";
import { SuggestSceneFormatsToolUI } from "./tools/SuggestSceneFormatsToolUI";
import { AddNarrationToolUI } from "./tools/AddNarrationToolUI";
import { EditImageToolUI } from "./tools/EditImageToolUI";
import { CreateAvatarToolUI } from "./tools/CreateAvatarToolUI";
import { AvatarFeaturesToolUI } from "./tools/AvatarFeaturesToolUI";
import { CreateVideoToolUI } from "./tools/CreateVideoToolUI";
import { HowToToolUI } from "./tools/HowToToolUI";
import {
  SetSceneFontToolUI,
  SetNarrationVoiceToolUI,
  AnimateAllImagesToolUI,
  MotionDirectorToolUI,
  RedesignSceneToolUI,
  EditMotionSceneToolUI,
} from "./tools/BulkMutateToolUI";
import { ChatCommitContext } from "./ChatCommitContext";
import { makeAddImageToScene } from "./commitMedia";
import GeneratedImagesStrip from "./GeneratedImagesStrip";
import BuildNotice from "./BuildNotice";
import ReviewTekno3Card from "./ReviewTekno3Card";
import { setBuildNotice, useBuildLocked, useBuildNotice } from "./buildMode";
import JobWidgetHost from "../../containers/JobWidgetHost";
import {
  SendIcon,
  PlusIcon,
  CloseIcon,
  PanelLeftIcon,
  ThreadIcon,
  TrashIcon,
  AttachIcon,
  SparklesIcon,
  ImageIcon,
  FileTextIcon,
  QuizIcon,
  InfographicIcon,
  ChatLauncherIcon,
  SoundIcon,
  AvatarIcon,
  VideoIcon,
  WandIcon,
  ActivityIcon,
} from "./icons";

// When the URL carries `aichatdemo=1` (in addition to `aichat=1`) we pre-seed
// the thread with a short static conversation that showcases the inline
// generative-UI preview cards, so the states can be screenshotted. Purely a
// visual demo — no runtime/backend involvement. Read once at module load.
const IS_CHAT_DEMO =
  typeof window !== "undefined" && /[?&]aichatdemo=1\b/.test(window.location.search);

// ---------------------------------------------------------------------------
// AG-UI endpoint resolution. The chat talks to a REAL AG-UI transport via
// assistant-ui's useAgUiRuntime + @ag-ui/client HttpAgent (see ChatPanel below)
// instead of the old in-memory echo adapter.
//
// When the editor is opened inside its host (a training is loaded), the real
// authed NestJS middleware endpoint is `window.ideaLibraryRootUrl + 'api/sa/agent'`
// (JWT-guarded, same AG-UI wire format) — that's the default. The endpoint can
// be overridden, in priority order, by:
//   1. a `?saurl=` URL param, then
//   2. a `window.AIDEA_SA_URL` global, then
//   3. `window.ideaLibraryRootUrl + 'api/sa/agent'` (the real authed Nest endpoint).
//
// When none of those resolve (e.g. `?aichat=1` opened standalone with no
// training and no dev override) there is no endpoint to talk to — returns
// null. Devs pointing at the standalone echo server
// (aidea-middleware/scripts/sa-echo-server.js) use `?saurl=http://localhost:4111/api/sa/agent`.
function resolveAgUiUrl() {
  if (typeof window === "undefined") return null;
  try {
    const fromParam = new URLSearchParams(window.location.search).get("saurl");
    if (fromParam) return fromParam;
  } catch {
    // URLSearchParams unavailable / malformed search — fall through.
  }
  if (typeof window.AIDEA_SA_URL === "string" && window.AIDEA_SA_URL) {
    return window.AIDEA_SA_URL;
  }
  // Real authed middleware endpoint: rootUrl + exactly one trailing slash +
  // "api/sa/agent" (e.g. "http://localhost:4000/" → ".../api/sa/agent").
  if (typeof window.ideaLibraryRootUrl === "string" && window.ideaLibraryRootUrl) {
    return `${window.ideaLibraryRootUrl.replace(/\/+$/, "")}/api/sa/agent`;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Client-side thread store (the spike). Real multi-thread switching is wired
// through the AG-UI runtime's experimental `adapters.threadList` slot (see
// useAgUiRuntime below). For the spike the list of threads lives entirely in
// React state — one `{ id, title, messages }` per conversation — with no server
// round-trip. The runtime restores a conversation by calling our
// `onSwitchToThread(id)` and replaying the `messages` we return into the view
// (it invokes `core.applyExternalMessages(result.messages)` internally).
//
// `messages` are stored in assistant-ui's normalized ThreadMessage shape — the
// exact type `applyExternalMessages(readonly ThreadMessage[])` expects — so the
// capture→store→restore round-trip needs no conversion. We capture them from
// the live thread via `useThread((t) => t.messages)` (MessageState, a structural
// superset of ThreadMessage; the runtime reads only the ThreadMessage fields).
//
// localStorage persistence: threads + activeId are saved to a per-training key
// so conversation history survives page refreshes.
const DEFAULT_THREAD_TITLE = "New conversation";

// Maximum number of threads to persist (trim oldest when exceeded).
const MAX_PERSISTED_THREADS = 30;

// Derive the localStorage key for a given training (or "global" when none).
function chatStorageKey() {
  const trainingId = (typeof window !== "undefined" && window.idealStudioTrainingId) || "global";
  return `aidea-chat:threads:${trainingId}`;
}

// Load persisted { threads, activeId } from localStorage.
// Returns null when absent, unreadable, or structurally invalid.
function loadPersisted() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(chatStorageKey());
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      !parsed ||
      !Array.isArray(parsed.threads) ||
      parsed.threads.length === 0 ||
      typeof parsed.activeId !== "string"
    ) {
      return null;
    }
    // Each thread must have an id string and a messages array.
    const threads = parsed.threads.filter(
      (t) => t && typeof t.id === "string" && Array.isArray(t.messages)
    );
    if (threads.length === 0) return null;
    // activeId must exist in the thread list.
    const activeId = threads.some((t) => t.id === parsed.activeId)
      ? parsed.activeId
      : threads[0].id;
    return { threads, activeId };
  } catch {
    return null;
  }
}

function newThreadId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID (older WebViews).
  return `t-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function makeThread() {
  return { id: newThreadId(), title: DEFAULT_THREAD_TITLE, messages: [] };
}

// Derive a short rail title from a thread's first user message (nice-to-have).
// Falls back to the default placeholder while the thread is still empty.
function deriveTitle(messages) {
  const firstUser = messages.find((m) => m.role === "user");
  if (!firstUser) return DEFAULT_THREAD_TITLE;
  const text = (firstUser.content || [])
    .filter((p) => p && p.type === "text" && typeof p.text === "string")
    .map((p) => p.text)
    .join(" ")
    .trim();
  if (!text) return DEFAULT_THREAD_TITLE;
  return text.length > 40 ? `${text.slice(0, 40).trimEnd()}…` : text;
}

// A persisted message item is { parentId, message }. Older data stored a bare
// ThreadMessage; normalize BOTH so restore can carry parentId (for future
// branched / regenerated histories) without dropping existing history. The
// AG-UI core reads only `.message` today, so this is forward-compatible and
// changes no current behaviour — it just preserves the parent link.
function normItem(x) {
  if (x && typeof x === "object" && x.message) {
    return { parentId: x.parentId ?? null, message: x.message };
  }
  return { parentId: null, message: x };
}

// Suggestion cards for the empty state. Title + prompt are resolved from i18n
// at render (see EmptyState) so both the visible card AND the prompt sent to
// the agent follow the editor's language.
const SUGGESTION_DEFS = [
  {
    id: "infographic",
    Icon: InfographicIcon,
    titleKey: "aideaChat.suggestions.infographicTitle",
    promptKey: "aideaChat.suggestions.infographicPrompt",
  },
  {
    id: "image",
    Icon: ImageIcon,
    titleKey: "aideaChat.suggestions.imageTitle",
    promptKey: "aideaChat.suggestions.imagePrompt",
  },
  {
    id: "narration",
    Icon: SoundIcon,
    titleKey: "aideaChat.suggestions.narrationTitle",
    promptKey: "aideaChat.suggestions.narrationPrompt",
  },
  {
    id: "avatar",
    Icon: AvatarIcon,
    titleKey: "aideaChat.suggestions.avatarTitle",
    promptKey: "aideaChat.suggestions.avatarPrompt",
  },
  {
    id: "video",
    Icon: VideoIcon,
    titleKey: "aideaChat.suggestions.videoTitle",
    promptKey: "aideaChat.suggestions.videoPrompt",
  },
  {
    id: "motion",
    Icon: WandIcon,
    titleKey: "aideaChat.suggestions.motionTitle",
    promptKey: "aideaChat.suggestions.motionPrompt",
  },
];

// Shared focus-ring utility: a violet ring that respects the brand accent.
export const FOCUS_RING =
  "focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[var(--aidea-primary)] focus-visible:tw-ring-offset-1 focus-visible:tw-ring-offset-white";

// ---------------------------------------------------------------------------
// Resize bounds + persistence. The panel is pinned to the bottom-right corner
// and grows toward the top-left, so the resize grip lives at the top-left.
// Bounds are hard limits; both axes are additionally clamped to the viewport
// (see clampSize) so the popup can never exceed the available space.
// ---------------------------------------------------------------------------
const SIZE_MIN_W = 380;
const SIZE_MIN_H = 460;
// Raised so the default (40% vw / 80% vh) is never clamped below target on
// normal screens; the viewport clamp in clampSize still prevents overflow.
const SIZE_MAX_W = 1100;
const SIZE_MAX_H = 1600;
const VIEWPORT_MARGIN = 16; // px gap kept between the panel and each viewport edge
// Bumped (…v2) so the new 40%×80% default applies even if an old size is stored.
const SIZE_STORAGE_KEY = "aideaChatSize2";

// Preferred default before viewport clamping: 40% of viewport width, 80% of
// viewport height (a roomy working panel; still drag-resizable + clamped).
function defaultSize() {
  const vw = typeof window !== "undefined" ? window.innerWidth : SIZE_MAX_W;
  const vh = typeof window !== "undefined" ? window.innerHeight : SIZE_MAX_H;
  return { width: Math.round(vw * 0.4), height: Math.round(vh * 0.8) };
}

// Clamp a {width,height} to the bounds AND to what the viewport can show
// (max-width: 100vw - margins, max-height: 86vh), keeping >= the min size.
function clampSize({ width, height }) {
  const vw = typeof window !== "undefined" ? window.innerWidth : SIZE_MAX_W;
  const vh = typeof window !== "undefined" ? window.innerHeight : SIZE_MAX_H;
  const maxW = Math.min(SIZE_MAX_W, Math.max(SIZE_MIN_W, vw - VIEWPORT_MARGIN * 2));
  const maxH = Math.min(SIZE_MAX_H, Math.max(SIZE_MIN_H, Math.round(vh * 0.86)));
  return {
    width: Math.round(Math.min(maxW, Math.max(SIZE_MIN_W, width))),
    height: Math.round(Math.min(maxH, Math.max(SIZE_MIN_H, height))),
  };
}

function readStoredSize() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(SIZE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.width === "number" && typeof parsed.height === "number") {
      return parsed;
    }
  } catch {
    // Ignore malformed/blocked storage — fall back to the default size.
  }
  return null;
}

// Drag-to-resize from the top-left corner. Returns the live size, a setter the
// grip's onPointerDown uses, and a `resizing` flag (suppresses text selection /
// transitions while dragging). Size is restored from and persisted to
// localStorage so it survives reloads; everything is viewport-clamped.
function useResizableSize() {
  const [size, setSize] = useState(() => clampSize(readStoredSize() || defaultSize()));
  const [resizing, setResizing] = useState(false);

  // Re-clamp on viewport resize so the panel never overflows after the window
  // shrinks (e.g. devtools opening). Does not grow a user-shrunk panel.
  useEffect(() => {
    const onResize = () => setSize((s) => clampSize(s));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Persist the chosen size (debounced via the natural drag-end cadence; we
  // just write whenever it changes — it's tiny).
  useEffect(() => {
    try {
      window.localStorage.setItem(SIZE_STORAGE_KEY, JSON.stringify(size));
    } catch {
      // Storage may be unavailable (private mode) — non-fatal.
    }
  }, [size]);

  const onResizeStart = useCallback(
    (e) => {
      e.preventDefault();
      // Anchor is the fixed bottom-right corner; moving the pointer toward the
      // top-left grows the panel. Track deltas from the pointer's start point.
      const startX = e.clientX;
      const startY = e.clientY;
      const startW = size.width;
      const startH = size.height;
      setResizing(true);

      const onMove = (ev) => {
        // Pointer moving left (-dx) or up (-dy) => bigger.
        const next = clampSize({
          width: startW + (startX - ev.clientX),
          height: startH + (startY - ev.clientY),
        });
        setSize(next);
      };
      const onUp = () => {
        setResizing(false);
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [size.width, size.height]
  );

  return { size, resizing, onResizeStart };
}

// ---------------------------------------------------------------------------
// FIX 1: Hook that tracks whether the chat should be in docked (slim rail) mode.
// Listens for the `aidea:popupDocked` window event dispatched by setPopupDocked.
// Returns true when body.aidea-popup-docked is set.
// ---------------------------------------------------------------------------
function usePopupDocked() {
  const [docked, setDocked] = useState(
    () => typeof document !== "undefined" && document.body.classList.contains("aidea-popup-docked")
  );
  useEffect(() => {
    const handler = (e) => setDocked(!!(e && e.detail && e.detail.docked));
    window.addEventListener("aidea:popupDocked", handler);
    return () => window.removeEventListener("aidea:popupDocked", handler);
  }, []);
  return docked;
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------
// A header tab pill. Active → violet fill; inactive → muted text. The chat tab
// doubles as the "back to chat" control.
function HeaderTab({ active, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`tw-rounded-full tw-border-none tw-px-3.5 tw-py-1 tw-text-[13px] tw-font-medium tw-transition-colors tw-duration-200 tw-cursor-pointer ${FOCUS_RING} ${
        active
          ? "tw-bg-[var(--ac-surface)] tw-text-[var(--ac-fg)] tw-shadow-sm"
          : "tw-bg-transparent tw-text-[var(--ac-muted)] hover:tw-text-[var(--ac-text)]"
      }`}
    >
      {label}
    </button>
  );
}

function Header({ onToggleRail, onClose, railOpen, activeTab, onTabChange }) {
  const onChat = activeTab === "chat";
  return (
    <header className="tw-flex tw-h-14 tw-flex-none tw-items-center tw-gap-2 tw-border-b tw-border-[var(--aidea-border)] tw-bg-[var(--aidea-surface)] tw-px-3">
      {/* Thread rail toggle is only meaningful in chat mode. */}
      {onChat ? (
        <IconButton label={railOpen ? "Hide threads" : "Show threads"} onClick={onToggleRail}>
          <PanelLeftIcon size={17} />
        </IconButton>
      ) : (
        <span aria-hidden="true" className="tw-h-8 tw-w-8 tw-flex-none" />
      )}

      <span className="tw-flex tw-h-7 tw-w-7 tw-flex-none tw-items-center tw-justify-center tw-rounded-lg tw-bg-[var(--ac-accent-tint)] tw-text-[var(--ac-accent)]">
        <SparklesIcon size={15} />
      </span>

      {/* Beta badge — newchat's "new" pill (solid brand accent, white bold). */}
      <span className="tw-flex-none tw-rounded-full tw-bg-[var(--ac-accent)] tw-px-1.5 tw-py-0.5 tw-text-[10px] tw-font-bold tw-text-[var(--aidea-on-primary)]">
        Beta
      </span>

      <div className="tw-flex tw-min-w-0 tw-items-center tw-rounded-full tw-bg-[var(--ac-hover)] tw-p-0.5">
        <HeaderTab
          active={onChat}
          label={i18n.t("aideaChat.assistant")}
          onClick={() => onTabChange("chat")}
        />
      </div>

      <div className="tw-ml-auto tw-flex tw-items-center tw-gap-1">
        <IconButton label="Close chat" onClick={onClose}>
          <CloseIcon size={17} />
        </IconButton>
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Thread rail (collapsible left rail inside the popup)
// ---------------------------------------------------------------------------
// buildEntry: { active, onSelect } — the finished build conversation as a read-only first entry.
function ThreadRail({ threads, activeId, onSelect, onNewThread, onDelete, buildEntry = null }) {
  return (
    <aside className="tw-flex tw-w-[148px] tw-flex-none tw-flex-col tw-gap-2 tw-border-r tw-border-[var(--aidea-border)] tw-bg-[var(--aidea-muted-bg)] tw-p-2">
      <button
        type="button"
        onClick={onNewThread}
        className={`tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-rounded-full tw-bg-[var(--ac-ink)] tw-px-2.5 tw-py-2 tw-text-[13px] tw-font-medium tw-text-[var(--aidea-on-primary)] tw-transition-opacity tw-duration-200 tw-cursor-pointer hover:tw-opacity-90 ${FOCUS_RING}`}
      >
        <PlusIcon size={15} />
        {i18n.t("aideaChat.newThread")}
      </button>

      <div className="tw-mt-1 tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-gap-1 tw-overflow-y-auto aidea-chat-scroll">
        {buildEntry && (
          <button
            type="button"
            onClick={buildEntry.onSelect}
            aria-current={buildEntry.active ? "true" : undefined}
            className={`tw-flex tw-w-full tw-items-start tw-gap-1.5 tw-rounded-lg tw-px-2 tw-py-1.5 tw-text-left tw-cursor-pointer tw-transition-colors tw-duration-200 ${FOCUS_RING} ${
              buildEntry.active
                ? "tw-bg-[var(--aidea-primary-tint)] tw-text-[var(--aidea-primary-hover)]"
                : "tw-text-[var(--aidea-fg)] hover:tw-bg-white"
            }`}
          >
            <ThreadIcon
              size={14}
              className="tw-mt-0.5 tw-flex-none tw-text-[var(--aidea-muted-fg)]"
            />
            <span className="tw-block tw-truncate tw-text-[12.5px] tw-font-medium tw-leading-tight">
              {i18n.t("aideaChat.courseBuild")}
            </span>
          </button>
        )}
        {threads.map((t) => {
          const active = t.id === activeId;
          const count = t.messages.length;
          const subtitle =
            count === 0
              ? i18n.t("aideaChat.empty")
              : i18n.t(count === 1 ? "aideaChat.messages" : "aideaChat.messagesPlural", { count });
          // Stored title is a stable English sentinel; translate it for display.
          const displayTitle =
            t.title === DEFAULT_THREAD_TITLE ? i18n.t("aideaChat.newConversation") : t.title;
          return (
            <div
              key={t.id}
              className={`tw-group tw-flex tw-w-full tw-items-stretch tw-rounded-lg tw-transition-colors tw-duration-200 ${
                active ? "tw-bg-[var(--aidea-primary-tint)]" : "hover:tw-bg-white"
              }`}
            >
              <button
                type="button"
                onClick={() => onSelect(t.id)}
                aria-current={active ? "true" : undefined}
                className={`tw-flex tw-min-w-0 tw-flex-1 tw-items-start tw-gap-1.5 tw-rounded-l-lg tw-px-2 tw-py-1.5 tw-text-left tw-cursor-pointer ${FOCUS_RING} ${
                  active ? "tw-text-[var(--aidea-primary-hover)]" : "tw-text-[var(--aidea-fg)]"
                }`}
              >
                <ThreadIcon
                  size={14}
                  className={
                    active
                      ? "tw-mt-0.5 tw-flex-none tw-text-[var(--aidea-primary)]"
                      : "tw-mt-0.5 tw-flex-none tw-text-[var(--aidea-muted-fg)]"
                  }
                />
                <span className="tw-min-w-0">
                  <span className="tw-block tw-truncate tw-text-[12.5px] tw-font-medium tw-leading-tight">
                    {displayTitle}
                  </span>
                  <span className="tw-block tw-truncate tw-text-[11px] tw-text-[var(--aidea-muted-fg)]">
                    {subtitle}
                  </span>
                </span>
              </button>
              <button
                type="button"
                aria-label="Delete thread"
                title="Delete thread"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(t.id);
                }}
                className={`tw-flex tw-w-7 tw-flex-none tw-items-center tw-justify-center tw-rounded-r-lg tw-text-[var(--aidea-muted-fg)] tw-opacity-0 tw-transition-opacity tw-duration-200 group-hover:tw-opacity-100 focus:tw-opacity-100 hover:tw-text-[#dc2626] ${FOCUS_RING}`}
              >
                <TrashIcon size={13} />
              </button>
            </div>
          );
        })}
      </div>
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Reusable icon button (40px touch target, focus ring, cursor-pointer)
// ---------------------------------------------------------------------------
export function IconButton({ label, onClick, children, tone = "default" }) {
  const toneCls =
    tone === "default"
      ? "tw-text-[var(--ac-muted)] hover:tw-bg-[var(--ac-hover)] hover:tw-text-[var(--ac-fg)]"
      : "tw-text-[var(--ac-muted)] hover:tw-bg-[var(--ac-hover)]";
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className={`tw-flex tw-h-8 tw-w-8 tw-flex-none tw-items-center tw-justify-center tw-rounded-lg tw-border-none tw-bg-transparent tw-transition-colors tw-duration-200 tw-cursor-pointer ${toneCls} ${FOCUS_RING}`}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Resize grip — a small handle in the top-left corner. Dragging it resizes the
// popup (which is anchored bottom-right). Pointer-events based, no deps.
// ---------------------------------------------------------------------------
function ResizeGrip({ onPointerDown }) {
  return (
    <div
      role="separator"
      aria-orientation="horizontal"
      aria-label="Resize chat panel"
      title="Drag to resize"
      onPointerDown={onPointerDown}
      className="aidea-chat-resize-grip tw-absolute tw-left-0 tw-top-0 tw-z-10 tw-flex tw-h-5 tw-w-5 tw-items-start tw-justify-start"
    >
      {/* Two short diagonal strokes hint at a NW-resize affordance. */}
      <svg
        width="14"
        height="14"
        viewBox="0 0 14 14"
        fill="none"
        aria-hidden="true"
        className="tw-m-1 tw-text-[var(--aidea-muted-fg)]"
      >
        <path
          d="M1 6 L6 1 M1 11 L11 1"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Suggestion card — clicking drops its prompt into the composer.
// ---------------------------------------------------------------------------
function SuggestionCard({ Icon, title, onPick }) {
  return (
    <button
      type="button"
      onClick={onPick}
      className={`tw-group tw-flex tw-w-full tw-items-center tw-gap-2.5 tw-rounded-lg tw-border tw-border-[var(--aidea-border)] tw-bg-[var(--aidea-surface)] tw-px-3 tw-py-2.5 tw-text-left tw-transition-colors tw-duration-200 tw-cursor-pointer hover:tw-border-[#d8b4fe] hover:tw-bg-[var(--ac-accent-tint)] ${FOCUS_RING}`}
    >
      <span className="tw-flex tw-h-8 tw-w-8 tw-flex-none tw-items-center tw-justify-center tw-rounded-lg tw-bg-[var(--aidea-primary-tint)] tw-text-[var(--aidea-primary)] tw-transition-colors tw-duration-200 group-hover:tw-bg-[var(--aidea-primary)] group-hover:tw-text-[var(--aidea-on-primary)]">
        <Icon size={16} />
      </span>
      <span className="tw-text-[13px] tw-font-medium tw-text-[var(--aidea-fg)]">{title}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Empty / greeting state — centered greeting + suggestion cards.
// Rendered inside ThreadPrimitive.Empty so it shows only with no messages.
// Uses the composer runtime to inject a suggestion's prompt into the input.
// ---------------------------------------------------------------------------
function EmptyState() {
  const composer = useComposerRuntime();
  const pick = useCallback(
    (prompt) => {
      composer.setText(prompt);
      composer.focus?.();
    },
    [composer]
  );

  return (
    <div className="tw-flex tw-h-full tw-flex-col tw-items-center tw-justify-center tw-px-4 tw-py-6">
      <span className="tw-mb-3 tw-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center tw-rounded-2xl tw-bg-[var(--aidea-primary-tint)] tw-text-[var(--aidea-primary)]">
        <SparklesIcon size={24} />
      </span>
      <h2 className="tw-text-[22px] tw-font-semibold tw-leading-tight tw-text-[var(--aidea-fg)]">
        {i18n.t("aideaChat.greetingTitle")}
      </h2>
      <p className="tw-mb-5 tw-mt-1 tw-text-[14px] tw-text-[var(--aidea-muted-fg)]">
        {i18n.t("aideaChat.greetingSubtitle")}
      </p>

      <div className="tw-grid tw-w-full tw-max-w-[320px] tw-grid-cols-1 tw-gap-2">
        {SUGGESTION_DEFS.map((s) => (
          <SuggestionCard
            key={s.id}
            Icon={s.Icon}
            title={i18n.t(s.titleKey)}
            onPick={() => pick(i18n.t(s.promptKey))}
          />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Messages
// ---------------------------------------------------------------------------
function UserMessage() {
  return (
    <MessagePrimitive.Root className="tw-flex tw-justify-end tw-py-2">
      <div className="tw-max-w-[85%] tw-rounded-2xl tw-bg-[var(--ac-surface)] tw-px-4 tw-py-2.5 tw-text-[14px] tw-leading-relaxed tw-text-[var(--ac-fg)] tw-shadow-sm tw-ring-1 tw-ring-[var(--ac-border)] tw-whitespace-pre-wrap tw-break-words">
        <MessagePrimitive.Parts />
      </div>
    </MessagePrimitive.Root>
  );
}

// FIX 3: AssistantMessageContent separates the text bubble from tool cards.
// When the message has no text parts (tool-call only), the empty bubble is
// suppressed so only the tool card shows. When there is text, the bubble renders
// as before. Tool-call parts (cards) always render outside the bubble via
// MessagePrimitive.Parts — the ToolUI registration from makeAssistantToolUI
// intercepts tool-call parts and renders them as standalone cards.
// Same assistant markdown as the build chat (BuildChat) and console /newchat.
const MarkdownText = ({ text }) => <>{renderMiniMarkdown(text)}</>;
const MARKDOWN_PARTS = { Text: MarkdownText };

function AssistantMessageContent() {
  const message = useMessage();
  const parts = (message && message.content) || [];
  const hasText = parts.some(
    (p) => p && p.type === "text" && typeof p.text === "string" && p.text.trim().length > 0
  );
  // Always render MessagePrimitive.Parts so tool cards (which hook into part
  // rendering) still appear. Only wrap in the bubble div when there is text.
  if (!hasText) {
    return <MessagePrimitive.Parts />;
  }
  return (
    <div className="tw-min-w-0 tw-text-[14px] tw-leading-relaxed tw-text-[var(--ac-text)] tw-whitespace-pre-wrap tw-break-words">
      <MessagePrimitive.Parts components={MARKDOWN_PARTS} />
    </div>
  );
}

function AssistantMessage() {
  return (
    <MessagePrimitive.Root className="tw-flex tw-flex-col tw-items-start tw-py-2">
      <AssistantMessageContent />
    </MessagePrimitive.Root>
  );
}

// ---------------------------------------------------------------------------
// Static demo bubbles (used only under ?aichatdemo=1). These mirror the look of
// the runtime-driven UserMessage / AssistantMessage above, but render plain
// children instead of <MessagePrimitive.Parts/> so we can drop a <PreviewCard/>
// inline. They never touch the runtime — this is purely a screenshot demo.
// ---------------------------------------------------------------------------
function DemoUserBubble({ children }) {
  return (
    <div className="tw-flex tw-justify-end tw-py-2">
      <div className="tw-max-w-[85%] tw-rounded-2xl tw-bg-[var(--ac-surface)] tw-px-4 tw-py-2.5 tw-text-[14px] tw-leading-relaxed tw-text-[var(--ac-fg)] tw-shadow-sm tw-ring-1 tw-ring-[var(--ac-border)] tw-whitespace-pre-wrap tw-break-words">
        {children}
      </div>
    </div>
  );
}

function DemoAssistantBubble({ children }) {
  // Wider cap than a plain text bubble (85%) so inline PreviewCards can use the
  // roomy message column; the card has its own max-width so it won't overgrow.
  return (
    <div className="tw-py-2">
      <div className="tw-min-w-0">{children}</div>
    </div>
  );
}

// The pre-seeded demo conversation: a user request, then an assistant reply
// that proposes a scene preview, then a second reply showing the image variant.
function DemoConversation() {
  return (
    <div className="tw-flex tw-flex-col tw-py-1">
      <DemoUserBubble>Create an infographic scene from this slide&apos;s narration.</DemoUserBubble>

      <DemoAssistantBubble>
        <div className="tw-text-[14px] tw-leading-relaxed tw-text-[var(--ac-text)]">
          Here&apos;s a 3-visual infographic synced to your narration — preview before adding:
        </div>
        <PreviewCard type="scene" />
      </DemoAssistantBubble>

      <DemoAssistantBubble>
        <div className="tw-text-[14px] tw-leading-relaxed tw-text-[var(--ac-text)]">
          Prefer a single hero image instead? Here&apos;s one generated from the same narration:
        </div>
        <PreviewCard type="image" />
      </DemoAssistantBubble>
    </div>
  );
}

// Typing indicator shown while the runtime is running (between send and reply).
function TypingIndicator() {
  const isRunning = useThread((t) => t.isRunning);
  if (!isRunning) return null;
  return (
    <div role="status" className="tw-flex tw-items-center tw-gap-1 tw-py-2">
      <span className="aidea-typing-dot" />
      <span className="aidea-typing-dot" />
      <span className="aidea-typing-dot" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Composer — auto-growing textarea, attach stub, violet Send (disabled empty).
// ---------------------------------------------------------------------------

// Seed the composer input from outside the chat (Image Animator's "Chatbot ile
// düzenle" button fires `aidea:seedChatInput` and stashes the text on
// window.__aideaChatSeed). Draining the stash on mount covers the common case
// where the panel is still opening when the event fires.
function useChatInputSeed() {
  const composer = useComposerRuntime();
  useEffect(() => {
    if (!composer) return undefined;
    const apply = (text) => {
      if (!text) return;
      composer.setText(text);
      composer.focus?.();
    };
    const handler = (e) => apply(e?.detail?.text);
    window.addEventListener("aidea:seedChatInput", handler);
    if (typeof window !== "undefined" && window.__aideaChatSeed) {
      const stashed = window.__aideaChatSeed;
      window.__aideaChatSeed = null;
      apply(stashed);
    }
    return () => window.removeEventListener("aidea:seedChatInput", handler);
  }, [composer]);
}

function Composer() {
  useChatInputSeed();
  return (
    <div className="tw-bg-[var(--ac-thread-bg)] tw-px-3 tw-pb-3 tw-pt-1">
      <ComposerPrimitive.Root className="tw-rounded-3xl tw-bg-[var(--ac-surface)] tw-p-2.5 tw-shadow-sm tw-ring-1 tw-ring-[var(--ac-border)] focus-within:tw-ring-[#d1d5db]">
        <ComposerPrimitive.Input
          autoFocus
          rows={1}
          submitOnEnter
          placeholder={i18n.t("aideaChat.placeholder")}
          className="tw-box-border tw-block tw-max-h-40 tw-min-h-[40px] tw-w-full tw-resize-none tw-border-none tw-bg-transparent tw-px-2 tw-py-1.5 tw-text-[14px] tw-leading-relaxed tw-text-[var(--ac-fg)] tw-outline-none placeholder:tw-text-[var(--ac-faint)]"
        />
        <div className="tw-flex tw-items-center tw-gap-1">
          <button
            type="button"
            aria-label="Attach a file (coming soon)"
            title="Attach a file (coming soon)"
            className={`tw-flex tw-h-9 tw-w-9 tw-flex-none tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-border-[var(--ac-border)] tw-bg-transparent tw-text-[var(--ac-faint)] tw-transition-colors tw-duration-200 tw-cursor-pointer hover:tw-bg-[var(--ac-hover)] hover:tw-text-[var(--ac-fg)] ${FOCUS_RING}`}
          >
            <AttachIcon size={17} />
          </button>
          <ComposerPrimitive.Send
            aria-label="Send message"
            title="Send message"
            className={`tw-ml-auto tw-flex tw-h-9 tw-w-9 tw-flex-none tw-items-center tw-justify-center tw-rounded-full tw-border-none tw-bg-[var(--ac-ink)] tw-text-[var(--aidea-on-primary)] tw-transition-opacity tw-duration-200 tw-cursor-pointer hover:tw-opacity-90 disabled:tw-cursor-not-allowed disabled:tw-opacity-30 ${FOCUS_RING}`}
          >
            <SendIcon size={16} />
          </ComposerPrimitive.Send>
        </div>
      </ComposerPrimitive.Root>
      <p className="tw-mb-0 tw-mt-1.5 tw-px-1 tw-text-center tw-text-[11px] tw-text-[var(--ac-faint)]">
        {i18n.t("aideaChat.hint")}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Thread (main area): viewport with empty-state / messages + composer
// ---------------------------------------------------------------------------
// The build chat (BuildChat) swaps in its own message/typing/composer look and adds
// rows at the end of the scrolling conversation (viewportTail). EmptyState={null}: no greeting.
export function Thread({
  children,
  EmptyState: Empty = EmptyState,
  AssistantMessage: Assistant = AssistantMessage,
  UserMessage: User = UserMessage,
  TypingIndicator: Typing = TypingIndicator,
  Composer: ComposerSlot = Composer,
  viewportTail = null,
} = {}) {
  return (
    <ThreadPrimitive.Root className="tw-flex tw-h-full tw-min-h-0 tw-flex-1 tw-flex-col tw-bg-[var(--ac-thread-bg)]">
      <ThreadPrimitive.Viewport className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-y-auto tw-px-4 tw-py-4 aidea-chat-scroll">
        {IS_CHAT_DEMO ? (
          // Static screenshot demo (?aichatdemo=1): pre-seeded conversation +
          // inline preview cards. Bypasses the empty greeting and the runtime.
          <DemoConversation />
        ) : (
          <>
            {Empty && (
              <ThreadPrimitive.Empty>
                <Empty />
              </ThreadPrimitive.Empty>
            )}

            <ThreadPrimitive.Messages
              components={{ UserMessage: User, AssistantMessage: Assistant }}
            />
            <Typing />
            {viewportTail}
          </>
        )}
      </ThreadPrimitive.Viewport>

      {/* Completed async image-generation jobs (from the same session_job_*
          socket events the Activity widget uses) surface here as cards with an
          "Add to scene" button. Renders nothing when there are none. */}
      {!IS_CHAT_DEMO && <GeneratedImagesStrip />}

      {children}
      <ComposerSlot />
    </ThreadPrimitive.Root>
  );
}

// ---------------------------------------------------------------------------
// ChatPanel — popup shell + runtime provider + layout
// ---------------------------------------------------------------------------
// buildThread: the build conversation (BuildChat's BuildThread), shown instead of the studio thread while build-locked.
export default function ChatPanel({ widgetProps, chatbotOptIn = false, buildThread = null }) {
  // Real AG-UI transport: HttpAgent POSTs an AG-UI RunAgentInput and consumes
  // the SSE event stream (RUN_STARTED → TEXT_MESSAGE_* → RUN_FINISHED).
  // The agent is created once (resolveAgUiUrl reads URL param / window global
  // at mount); useAgUiRuntime wires it into the assistant-ui runtime.
  // The real middleware endpoint is JWT-guarded (@UseGuards(AuthGuard)). The
  // Authorization header is NOT set here: this agent outlives the access token,
  // so agUiFetch reads the current token per request (and renews + re-sends on
  // a 401). It is omitted when there's no token so the unauthenticated
  // echo-server path still works.
  // Pass an explicit fetch: @ag-ui/client's HttpAgent stores the fetch
  // reference and calls it unbound, which throws "Illegal invocation" in
  // Studio's webpack bundle with a bare window.fetch.
  const agent = useMemo(() => {
    // The training the user is editing (set from the editor's `?id=` param).
    // Sent as a header so the agent can build a per-training knowledge base
    // (source docs, narrations, prior prompts, style) without coupling to the
    // AG-UI request body schema. Stable for the editing session.
    const trainingId = typeof window !== "undefined" ? window.idealStudioTrainingId : undefined;
    // The currently active scene/navItem id. Read from widgetProps (passed from
    // EditorApp's Redux-connected state.undoGroup.present.navItemSelected) at
    // agent-creation time. Re-created on widgetProps change so the header stays
    // current as the user navigates scenes. Best-effort: absent when widgetProps
    // is not available (standalone / demo mode).
    const navId =
      widgetProps && widgetProps.navItemSelected ? String(widgetProps.navItemSelected) : undefined;
    // The editor's active UI language ("en" | "es" | "tr" — load:"languageOnly"
    // so it's a bare 2-letter code). Sent so the agent replies in the editor's
    // language instead of mirroring whatever language the user happened to type.
    const editorLanguage = i18n && i18n.language ? String(i18n.language).slice(0, 2) : undefined;
    const headers = {};
    if (trainingId) headers["X-Aidea-Training-Id"] = String(trainingId);
    if (navId) headers["X-Aidea-Nav-Id"] = navId;
    if (editorLanguage) headers["X-Aidea-Editor-Language"] = editorLanguage;
    return new HttpAgent({
      url: resolveAgUiUrl(),
      headers: Object.keys(headers).length ? headers : undefined,
      // Per request: current Bearer token, selection + recent-edit context
      // headers, and renew-and-resend on a 401 — see agUiFetch.
      fetch: agUiFetch,
    });
  }, [widgetProps]);

  // Start CLOSED — the editor opens to a single launcher FAB; the user clicks it
  // (or the navbar Agent button) to open the chat. Don't auto-open on load.
  const [open, setOpen] = useState(false);
  // Tek launcher FAB tıklanınca açılan seçim menüsü (Widget mı / AI Assistant mı).
  const [launcherMenuOpen, setLauncherMenuOpen] = useState(false);
  // Bağımsız widget'ı (JobWidgetHost overlay) menüden açmak için sinyal: her artışta
  // openSignal artırılır ve AsyncWidgetApp'a prop olarak geçip tray'i kendi
  // tasarımıyla açar; asistan paneline girmez.
  const [widgetOpenSignal, setWidgetOpenSignal] = useState(0);
  // Which pane the content area shows: the chat thread or the embedded Activity
  // (async job) widget. The "Aidea Assistant" tab doubles as the back-to-chat
  // control. Default to chat.
  const [activeTab, setActiveTab] = useState("chat");
  // Rail defaults collapsed so the message column is roomy on first open; the
  // user toggles it from the header. The new default width (480) plus a roomy
  // message column lets the scene card's 4-button action bar fit cleanly.
  const [railOpen, setRailOpen] = useState(false);
  const { size, resizing, onResizeStart } = useResizableSize();

  // Open the assistant from the navbar "Agent" button (additive to the FAB).
  useEffect(() => {
    const openIt = () => {
      setOpen(true);
      setActiveTab("chat");
    };
    window.addEventListener("aidea:openAssistant", openIt);
    return () => window.removeEventListener("aidea:openAssistant", openIt);
  }, []);
  // Live build: the first notice (editing frozen) opens the assistant once; the pause card opens it again.
  const buildNotice = useBuildNotice();
  const hasBuildNotice = !!buildNotice;
  const buildPaused = !!buildNotice?.pause;
  const showBuildThread = useBuildLocked() && !!buildThread;
  // After the build: the build conversation stays reachable read-only from the rail ("Course build").
  const [viewBuild, setViewBuild] = useState(false);
  useEffect(() => {
    if (hasBuildNotice) setOpen(true);
  }, [hasBuildNotice, buildPaused]);
  // FIX 1: docked = true when a gateway popup is open; chat becomes a slim right rail.
  const docked = usePopupDocked();

  // Reflect the chat's open state on <body> so the popup-dock shift (chat.css)
  // only fires when the chat is actually open (a rail to clear). Collapsed to the
  // launcher → no `aidea-chat-open` → popups stay centred on the full screen.
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    document.body.classList.toggle("aidea-chat-open", open);
    return () => document.body.classList.remove("aidea-chat-open");
  }, [open]);

  // Re-flow the docked rail width on window resize. The docked shellStyle reads
  // window.innerWidth at compute time; without this it only recomputes on
  // dock-toggle, so resizing while a popup is open wouldn't adjust the rail
  // (the CSS popup-shift IS already live-responsive). rAF-debounced; only while
  // docked, so the common non-docked case pays nothing.
  const [viewportTick, setViewportTick] = useState(0);
  useEffect(() => {
    if (!docked) return undefined;
    let raf = 0;
    const onResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setViewportTick((t) => t + 1));
    };
    window.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
    };
  }, [docked]);

  // -------------------------------------------------------------------------
  // Real multi-thread state (the spike). `threads` is the client-side store;
  // `activeId` is the live thread the runtime is bound to via the threadList
  // adapter's `threadId`. We seed exactly one empty thread so the rail and the
  // runtime agree on a starting thread id from the first render.
  //
  // On first render we try to restore from localStorage (same training key);
  // if absent/invalid we fall back to a fresh empty thread.
  // -------------------------------------------------------------------------
  const [threads, setThreads] = useState(() => {
    const persisted = loadPersisted();
    return persisted ? persisted.threads : [makeThread()];
  });
  const [activeId, setActiveId] = useState(() => {
    const persisted = loadPersisted();
    return persisted ? persisted.activeId : threads[0].id;
  });

  // Keep the active id + thread list in refs so the history/threadList adapters
  // (created once, then closed over) always read the live values without being
  // re-created on every change.
  const activeIdRef = useRef(activeId);
  activeIdRef.current = activeId;
  const threadsRef = useRef(threads);
  threadsRef.current = threads;

  // Persist { threads, activeId } to localStorage. Messages live inside each
  // thread object (kept current by the history adapter's append below), so this
  // single write captures the whole conversation. No debounce: append fires only
  // a couple times per turn (the user message + the COMPLETED assistant message),
  // not per streamed token, so there's nothing to throttle.
  const persistAll = useCallback(() => {
    try {
      const all = threadsRef.current;
      const trimmed =
        all.length > MAX_PERSISTED_THREADS ? all.slice(all.length - MAX_PERSISTED_THREADS) : all;
      const outId = activeIdRef.current;
      const savedActiveId = trimmed.some((t) => t.id === outId)
        ? outId
        : trimmed[trimmed.length - 1].id;
      window.localStorage.setItem(
        chatStorageKey(),
        JSON.stringify({ threads: trimmed, activeId: savedActiveId })
      );
    } catch {
      // Quota exceeded or serialization failure — non-fatal, history won't save.
    }
  }, []);

  // Save on every threads/activeId change. Because append folds messages into
  // `threads` state, this effect runs (and persists) as soon as React commits
  // the new message — no separate debounce/flush path needed.
  useEffect(() => {
    persistAll();
  }, [threads, activeId, persistAll]);

  // -------------------------------------------------------------------------
  // Persistence via the AG-UI runtime's official `history` adapter (verified
  // against react-ag-ui@0.0.35 AgUiThreadRuntimeCore). The core drives it:
  //   - load() is called ONCE on mount (core.__internal_load). We return the
  //     active thread's stored messages as an ExportedMessageRepository
  //     ({ messages: [{ parentId, message }] }); the core applies them to the
  //     view via applyExternalMessages. This is the reliable restore path that
  //     replaces the old "bounce through a throwaway thread id" hack.
  //   - append({ parentId, message }) is called for every user message and every
  //     COMPLETED assistant message. We fold it into the active thread's messages
  //     in React state, so the rail title updates and the persist effect saves it.
  // The previous useThread-capture + 600ms debounce + mount-bounce machinery is
  // gone — this is the framework's blessed save+restore path.
  // -------------------------------------------------------------------------
  const historyAdapter = useMemo(
    () => ({
      load: async () => {
        const t = threadsRef.current.find((x) => x.id === activeIdRef.current);
        const msgs = (t && t.messages) || [];
        if (!msgs.length) return null;
        // Normalize (handles legacy bare-message items) → { parentId, message }.
        return { messages: msgs.map(normItem) };
      },
      append: async (item) => {
        const message = item && item.message;
        if (!message || !message.id) return;
        const parentId = item.parentId ?? null;
        setThreads((prev) =>
          prev.map((t) => {
            if (t.id !== activeIdRef.current) return t;
            const cur = t.messages || [];
            const idx = cur.findIndex((x) => normItem(x).message?.id === message.id);
            const nextItem = { parentId, message };
            let messages;
            if (idx >= 0) {
              // Same id re-appended (e.g. an assistant message finalised after a
              // prior partial record) — replace in place rather than duplicate.
              messages = cur.slice();
              messages[idx] = nextItem;
            } else {
              messages = [...cur, nextItem];
            }
            const title =
              t.title === DEFAULT_THREAD_TITLE
                ? deriveTitle(messages.map((x) => normItem(x).message)) || t.title
                : t.title;
            return { ...t, messages, title };
          })
        );
      },
    }),
    []
  );

  // "+ New Thread": mint a fresh empty thread and make it active. The runtime
  // clears the view itself (applyExternalMessages([])).
  const handleNewThread = useCallback(() => {
    const next = makeThread();
    // Update the ref synchronously so a history append() firing before React
    // re-renders folds into the NEW thread, not the one we just left.
    activeIdRef.current = next.id;
    setThreads((prev) => [...prev, next]);
    setActiveId(next.id);
  }, []);

  // Switch to an existing thread: make it active and RETURN its stored messages
  // so the runtime restores them via core.applyExternalMessages. Messages are
  // already current in `threads` (the history adapter keeps them so), so there's
  // nothing to snapshot on the way out.
  const handleSwitchToThread = useCallback((id) => {
    // Sync the ref before React re-renders so an append() in the switch window
    // targets the thread we're switching TO, not the one we left.
    activeIdRef.current = id;
    setActiveId(id);
    const target = threadsRef.current.find((t) => t.id === id);
    // Return a flat ThreadMessage[] for applyExternalMessages (unwrap items).
    return { messages: (target?.messages || []).map((x) => normItem(x).message) };
  }, []);

  // Experimental AG-UI threadList adapter. Shape verified against the installed
  // react-ag-ui@0.0.35 types (UseAgUiThreadListAdapter):
  //   { threadId?, onSwitchToNewThread?, onSwitchToThread?: (id) =>
  //       { messages: readonly ThreadMessage[]; state? } | Promise<…> }
  // `threadId` keys the runtime to the active thread; the two callbacks drive
  // create + switch. Memoized on activeId so the runtime always sees the live
  // thread id (the handlers are stable).
  const threadListAdapter = useMemo(
    () => ({
      threadId: activeId,
      onSwitchToNewThread: handleNewThread,
      onSwitchToThread: handleSwitchToThread,
    }),
    [activeId, handleNewThread, handleSwitchToThread]
  );

  const runtime = useAgUiRuntime({
    agent,
    adapters: { threadList: threadListAdapter, history: historyAdapter },
  });

  // Delete a thread from the rail. If it's the ACTIVE thread, first switch the
  // runtime to the most-recent remaining thread (so the view restores its
  // messages), or to a fresh empty thread when it was the last one — then drop it
  // from state. The persist effect writes the new thread list to localStorage.
  const handleDeleteThread = useCallback(
    (id) => {
      const wasActive = id === activeIdRef.current;
      const remaining = threadsRef.current.filter((t) => t.id !== id);
      if (wasActive) {
        if (remaining.length) {
          runtime.threads.switchToThread(remaining[remaining.length - 1].id);
        } else {
          runtime.threads.switchToNewThread();
        }
      }
      setThreads((prev) => prev.filter((t) => t.id !== id));
    },
    [runtime]
  );

  // Build finished with scenes: a fresh studio thread opens with the ready guidance as its first
  // assistant message (stored like any other, no agent call) instead of the empty welcome.
  const readyScenes = (buildNotice && buildNotice.ready && buildNotice.scenes) || 0;
  const readySeeded = useRef(false); // BuildChat re-sends the notice on any later feed change
  useEffect(() => {
    if (!readyScenes) return;
    setBuildNotice(null); // the message replaces the transient ready notice
    if (readySeeded.current) return;
    readySeeded.current = true;
    const next = makeThread();
    const message = fromThreadMessageLike(
      {
        role: "assistant",
        content: [
          {
            type: "text",
            text: i18n.t("aideaChat.buildNotice.readyMessage", { count: readyScenes }),
          },
        ],
      },
      `build-ready-${next.id}`,
      { type: "complete", reason: "unknown" }
    );
    next.messages = [{ parentId: null, message }];
    threadsRef.current = [...threadsRef.current, next]; // read synchronously by handleSwitchToThread
    setThreads((prev) => [...prev, next]);
    setViewBuild(false);
    runtime.threads.switchToThread(next.id);
  }, [readyScenes, runtime]);

  // Commit capabilities exposed to the chat's tool-UIs via ChatCommitContext.
  // makeAssistantToolUI components render without props, so the generateImage
  // card reads `addImageToScene` from context instead. Built from the same
  // `widgetProps` the job widget uses to add media to the scene; memoized so the
  // context value (and thus the bound commit fn) is stable across renders.
  const commitValue = useMemo(
    () => ({
      addImageToScene: makeAddImageToScene(widgetProps),
      // In-place swap of an existing image box's URL — the apply path for the
      // inline "edit this image with the chatbot" flow (EditImageToolUI). Same
      // toolbar-update call EditorShortcuts.onAcceptAnimation uses for a static
      // image result. Throws when the editor props aren't wired (job widget off)
      // so the card can surface "editor not ready".
      updateBoxImageUrl: (boxId, url) => {
        const fn = widgetProps?.onToolbarUpdated;
        if (typeof fn !== "function") throw new Error("editor not ready (no onToolbarUpdated)");
        fn(boxId, "main", "state", "url", url);
      },
      // Small per-tool-call card state (Motion job lifecycle) kept ON the thread, so
      // it persists with the history (the persist effect writes on every change).
      getToolState: (key) =>
        ((threadsRef.current.find((t) => t.id === activeIdRef.current) || {}).toolState || {})[
          key
        ] || null,
      setToolState: (key, value) =>
        setThreads((prev) => {
          const owner =
            prev.find((t) => t.toolState && key in t.toolState) ||
            prev.find((t) => t.id === activeIdRef.current);
          return prev.map((t) =>
            t === owner ? { ...t, toolState: { ...t.toolState, [key]: value } } : t
          );
        }),
    }),
    [widgetProps]
  );

  // Outer positioning + user-chosen size. Inline styles only — everything
  // visual is Tailwind inside `.aidea-chat-root`. `maxWidth/maxHeight` mirror
  // the clamp so the panel can never paint outside the viewport even for one
  // frame; the size state is already viewport-clamped.
  // FIX 1: when docked, override to a slim rail pinned full-height on the right.
  // Width is responsive (~22% of the viewport, clamped 260–340px) so it adapts to
  // the screen; the studio popup shifts left by this same amount (chat.css
  // .modal-dialog rule, clamp(260px,22vw,340px)) so the two never overlap.
  const shellStyle = useMemo(() => {
    if (docked) {
      const vw = typeof window !== "undefined" ? window.innerWidth : 1280;
      const vh = typeof window !== "undefined" ? window.innerHeight : 800;
      const railW = Math.round(Math.min(340, Math.max(260, vw * 0.22)));
      return {
        position: "fixed",
        right: 0,
        top: VIEWPORT_MARGIN,
        bottom: VIEWPORT_MARGIN,
        width: railW,
        height: vh - VIEWPORT_MARGIN * 2,
        maxWidth: railW,
        maxHeight: `calc(100vh - ${VIEWPORT_MARGIN * 2}px)`,
        zIndex: 100000,
      };
    }
    return {
      position: "fixed",
      right: VIEWPORT_MARGIN,
      bottom: VIEWPORT_MARGIN,
      width: size.width,
      height: size.height,
      maxWidth: `calc(100vw - ${VIEWPORT_MARGIN * 2}px)`,
      maxHeight: "86vh",
      zIndex: 100000,
    };
  }, [docked, size.width, size.height, viewportTick]);

  // When closed, the panel collapses to a single launcher FAB pinned bottom-right
  // (replaces JobWidgetHost's old purple FAB — same gradient/glow for continuity,
  // but a chat-assistant icon). Clicking it re-opens the panel.
  // Bağımsız Activity/Job widget — AI Assistant'tan tamamen AYRI bir üründür.
  // JobWidgetHost artık widget'ı editörün React ağacına native mount eder
  // (iframe yok); ama kendi FAB'ını GİZLİYORUZ (hideFab) çünkü tek giriş
  // noktası aşağıdaki launcher menüsü. Menüden "Widget" seçilince
  // widgetOpenSignal artar → AsyncWidgetApp'a openSignal prop'u olarak geçip
  // tray'i kendi tasarımıyla açar. Fragment'in ilk çocuğu olarak hem kapalı
  // hem açık durumda sabit kalır → widget remount edilmez.
  const standaloneWidget = widgetProps ? (
    <JobWidgetHost {...widgetProps} hideFab openSignal={widgetOpenSignal} />
  ) : null;

  // FAB tıklanınca menü açılır: "Chatbot" (AI Assistant sohbet paneli) ya da
  // "Widget" (JobWidgetHost'un native mount ettiği job tray) seçilebilir. Global Config'teki
  // "Chatbot" toggle'ı (default açık) kapatılırsa menü atlanır ve FAB direkt
  // widget'ı açar (widget mı chatbot mu sorulmaz).
  const SHOW_AI_ASSISTANT = chatbotOptIn !== false;
  // Tek launcher FAB → tıklanınca menü: "Widget" mı "AI Assistant" mı? Seçilen
  // KENDİ tasarımıyla ayrı açılır (widget = overlay, asistan = sohbet paneli).
  const openAssistant = () => {
    setActiveTab("chat");
    setOpen(true);
    setLauncherMenuOpen(false);
  };
  const openWidget = () => {
    setWidgetOpenSignal((s) => s + 1);
    setLauncherMenuOpen(false);
  };
  // Menü artık tek bir kart; her seçenek kartın içinde kenarlıksız/gölgesiz bir
  // satır (iki ayrı yüzen pill gibi görünmesin diye).
  const menuItemStyle = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 14px",
    border: "none",
    background: "transparent",
    color: "#1f2937",
    fontWeight: 600,
    fontSize: 13,
    cursor: "pointer",
    whiteSpace: "nowrap",
    width: "100%",
    textAlign: "left",
  };
  const launcher = (
    <>
      {launcherMenuOpen && (
        <div
          role="menu"
          style={{
            position: "fixed",
            right: VIEWPORT_MARGIN,
            bottom: VIEWPORT_MARGIN + 60,
            zIndex: 100001,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            borderRadius: 14,
            border: "1px solid #e2e8f0",
            background: "#ffffff",
            boxShadow: "0px 8px 20px rgba(0,0,0,0.15)",
          }}
        >
          {SHOW_AI_ASSISTANT && (
            <button type="button" role="menuitem" onClick={openAssistant} style={menuItemStyle}>
              <span style={{ lineHeight: 0, color: "#a855f7" }}>
                <ChatLauncherIcon size={18} />
              </span>
              Chatbot
            </button>
          )}
          {widgetProps && (
            <button type="button" role="menuitem" onClick={openWidget} style={menuItemStyle}>
              <ActivityIcon size={16} />
              Widget
            </button>
          )}
        </div>
      )}
      <button
        type="button"
        aria-label="Open Aidea Assistant"
        title="Open Aidea Assistant"
        aria-haspopup="menu"
        aria-expanded={launcherMenuOpen}
        onClick={() => {
          // Asistan gizliyken menüye gerek yok: FAB direkt widget'ı açar.
          if (!SHOW_AI_ASSISTANT) {
            openWidget();
            return;
          }
          setLauncherMenuOpen((v) => !v);
        }}
        className={FOCUS_RING}
        style={{
          position: "fixed",
          right: VIEWPORT_MARGIN,
          bottom: VIEWPORT_MARGIN,
          width: 48,
          height: 48,
          borderRadius: 9999,
          border: "none",
          padding: 0,
          cursor: "pointer",
          zIndex: 100000,
          background: "linear-gradient(135deg, #a855f7, #d946ef)",
          boxShadow: "0px 10px 25px rgba(0,0,0,0.25)",
          display: "grid",
          placeItems: "center",
        }}
      >
        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: 9999,
            filter: "blur(12px)",
            opacity: 0.7,
            background: "radial-gradient(circle at 30% 30%, rgba(168,85,247,0.9), transparent 60%)",
          }}
        />
        <ActivityIcon size={22} style={{ position: "relative", zIndex: 1 }} aria-hidden="true" />
      </button>
    </>
  );

  // Kapalıyken: bağımsız widget overlay'i (gizli FAB) + tek launcher menüsü.
  if (!open) {
    return (
      <>
        {standaloneWidget}
        <div className="aidea-chat-root">{launcher}</div>
      </>
    );
  }

  return (
    <>
      {standaloneWidget}
      <div
        className={`aidea-chat-root${resizing ? " aidea-chat-resizing" : ""}`}
        style={shellStyle}
      >
        <AssistantRuntimeProvider runtime={runtime}>
          <ChatCommitContext.Provider value={commitValue}>
            {/* Message persistence (save + restore) is handled by the AG-UI
            runtime's `history` adapter wired in ChatPanel — no capture component
            needed here. */}
            {/* Registers the branded renderer for the agent's `recommendTrainingFormats`
            tool call. Rendering this component (inside the runtime provider) is how
            assistant-ui's makeAssistantToolUI hooks the renderer up — it paints no
            chrome itself; the card is emitted inline within the matching tool part. */}
            {!IS_CHAT_DEMO && <RecommendFormatsToolUI />}
            {/* Same registration mechanism for the `generateImage` tool: an image
            preview card with an "Add to scene" button that commits the image into
            the current scene via addImageToScene (from ChatCommitContext). */}
            {!IS_CHAT_DEMO && <GenerateImageToolUI />}
            {!IS_CHAT_DEMO && <SuggestImageStylesToolUI />}
            {!IS_CHAT_DEMO && <SuggestInfographicToolUI />}
            {!IS_CHAT_DEMO && <SuggestSceneFormatsToolUI />}
            {!IS_CHAT_DEMO && <AddNarrationToolUI />}
            {!IS_CHAT_DEMO && <EditImageToolUI />}
            {!IS_CHAT_DEMO && <CreateAvatarToolUI />}
            {!IS_CHAT_DEMO && <AvatarFeaturesToolUI />}
            {!IS_CHAT_DEMO && <CreateVideoToolUI />}
            {/* Coach / How-to card: numbered steps from the matching help guide,
            with screenshots lazy-loaded from the out-of-loop /howto/media endpoint.
            Display-only — no popup/gateway. */}
            {!IS_CHAT_DEMO && <HowToToolUI />}
            {/* Bulk-mutate cards: font/voice on all scenes (instant + Undo) and
            animate-all-images (gated confirm + progress). One shared card body,
            three tool-name registrations. */}
            {!IS_CHAT_DEMO && <SetSceneFontToolUI />}
            {!IS_CHAT_DEMO && <SetNarrationVoiceToolUI />}
            {!IS_CHAT_DEMO && <AnimateAllImagesToolUI />}
            {!IS_CHAT_DEMO && <MotionDirectorToolUI />}
            {!IS_CHAT_DEMO && <RedesignSceneToolUI />}
            {!IS_CHAT_DEMO && <EditMotionSceneToolUI />}
            <div className="tw-relative tw-flex tw-h-full tw-flex-col tw-overflow-hidden tw-rounded-2xl tw-border tw-border-[var(--aidea-border)] tw-bg-[var(--aidea-surface)] tw-shadow-lg">
              <ResizeGrip onPointerDown={onResizeStart} />
              <Header
                railOpen={railOpen}
                onToggleRail={() => setRailOpen((v) => !v)}
                onClose={() => setOpen(false)}
                activeTab={activeTab}
                onTabChange={setActiveTab}
              />

              {/* AI Assistant artık yalnızca sohbet; Activity/widget tamamen ayrı
                bağımsız bir üründür (kendi launcher'ı ile). */}
              <div className="tw-flex tw-min-h-0 tw-flex-1">
                {railOpen && !showBuildThread && (
                  <ThreadRail
                    threads={threads}
                    activeId={viewBuild ? null : activeId}
                    onSelect={(id) => {
                      setViewBuild(false);
                      runtime.threads.switchToThread(id);
                    }}
                    onNewThread={() => {
                      setViewBuild(false);
                      runtime.threads.switchToNewThread();
                    }}
                    onDelete={handleDeleteThread}
                    buildEntry={
                      buildThread ? { active: viewBuild, onSelect: () => setViewBuild(true) } : null
                    }
                  />
                )}
                {showBuildThread || (viewBuild && buildThread) ? (
                  buildThread
                ) : (
                  <Thread>
                    <BuildNotice />
                    {/* tekno3 scene review: a quick action above the composer, then the review in place.
                    Keyed by scene so switching scenes drops a review (and its fixes) of the previous one. */}
                    {!IS_CHAT_DEMO && (
                      <ReviewTekno3Card
                        key={String((widgetProps && widgetProps.navItemSelected) || "")}
                        navId={String((widgetProps && widgetProps.navItemSelected) || "")}
                      />
                    )}
                  </Thread>
                )}
              </div>
            </div>
          </ChatCommitContext.Provider>
        </AssistantRuntimeProvider>
      </div>
    </>
  );
}
